import React, { useCallback, useContext, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import type { AppDispatch } from 'src/client/store'
import styled from 'styled-components'
import { rem } from 'polished'
import { fireInteractionEvent } from 'api/googleTagManager'
import TrackingContext, { TrackingProps } from 'contexts/trackingContext'

import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL, OFFER_TYPE_LAST_MINUTE, OFFER_TYPE_TOUR } from 'constants/offer'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { accountAccessShowLogin } from 'actions/UiActions'
import TextLink from 'components/Luxkit/TextLink'
import WalledContent from 'components/Common/WalledContent'
import BundledHotelPriceContent from './BundledHotelPriceContent'
import HotelPriceContent from './HotelPriceContent'
import { GAProductFieldObject } from 'analytics/eventDefinitions'
import { productClick } from 'analytics/enhanced-ecommerce'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import { DistanceUnit } from 'components/OfferList/OfferListTiles/Support/searchDistanceLabel'
import { getDistanceLabel } from 'lib/recommendationUtils'
import Image from 'components/Common/Image'
import cn from 'clsx'
import Group from 'components/Luxkit/Group'

const Section = styled(Group)`
  padding: ${rem(12)} ${rem(16)};
`

const DepositTagWrapper = styled.div`
  margin-top: ${rem(4)};
`

const WalledGardentWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  align-items: flex-start;
`

const Root = styled.div`
  width: ${rem(368)};
  display: grid;
  grid-template-columns: ${rem(100)} 1fr;
  background-color: ${props => props.theme.palette.neutral.eight};
  min-height: ${rem(100)};
  height: 100%;
  overflow: hidden;
  box-shadow: none;
  transition: outline 0.2s, box-shadow 0.2s;
  
  &:hover {
    box-shadow: ${props => props.theme.shadow.bottom.small};
  }
  
  &.tile-border {
    border: 1px solid ${props => props.theme.palette.neutral.five};
    border-radius: ${props => props.theme.borderRadius.small};
  }

  .input-keyboard &:focus {
    outline: 2px solid  ${props => props.theme.palette.neutral.five};
    outline-offset: 2px;
  }
`

const OfferTitleText = styled(BodyText)`
  max-width: ${rem(188)};
  ${mediaQueryUp.sm} {
    max-width: ${rem(220)};
  }
`

const OfferLocationText = styled(BodyText)`
  max-width: ${rem(188)};
  ${mediaQueryUp.sm} {
    max-width: ${rem(220)};
  }
`

const validOfferTypes: Array<App.OfferType> = [
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_LAST_MINUTE,
  OFFER_TYPE_TOUR,
  'direct_tour',
  'partner_tour',
  'connection_tour',
  OFFER_TYPE_BED_BANK,
]

export function shouldDisplay(offerType: App.OfferType) {
  return validOfferTypes.includes(offerType)
}

interface BaseProps {
  category: string
  currency: string
  image: App.Image
  title: string
  location: string
  name: string
  offerId: string
  offerType: App.OfferType
  position: number
  tracking: TrackingProps
  testId: string
  url: string
}

interface FlightProps {
  bundledWithFlightsOnly?: boolean
  flightOrigin?: string
  flightPrices?: { [airportCode: string]: number }
}

interface PricingProps {
  duration: string
  price: number
  saleUnit: string
}

interface OfferProps {
  distance?: number,
  distanceFrom?: string,
  distanceUnit?: DistanceUnit,
  depositType?: Tours.V2OfferDepositType,
  depositAmount?: number,
  depositAmountPercentage?: number,
  depositThresholds?: App.DepositThreshold,
  depositShouldDisplay: boolean,
  flights?: FlightProps
  removeTileBorder?: boolean;
  shouldDisplayValue?: boolean
  value?: number
  walledGarden?: boolean
}

interface OfferAndTourV2Props {
  pricing?: PricingProps
}

function YouMayAlsoLikeTile({
  category,
  currency,
  distance,
  distanceFrom,
  distanceUnit,
  depositType,
  depositAmount,
  depositAmountPercentage,
  depositShouldDisplay,
  depositThresholds,
  flights,
  image,
  title,
  location,
  name,
  removeTileBorder = false,
  offerId,
  offerType,
  position,
  pricing,
  shouldDisplayValue,
  testId,
  tracking,
  url,
  value,
  walledGarden,
}: BaseProps & OfferProps & OfferAndTourV2Props) {
  const dispatch = useDispatch<AppDispatch>()
  const trackingCtx = useContext(TrackingContext)
  const { price, saleUnit, duration } = pricing || {}

  const fireProductClick = useCallback(() => {
    const productClickObject: GAProductFieldObject = {
      id: offerId,
      name,
      category,
      price,
      quantity: 1,
      position,
    }
    const extras = tracking?.lere ? {
      lereVersion: tracking.lere.version,
    } : {}
    productClick(currency, productClickObject, {
      listId: tracking?.listId,
      listName: tracking?.listName,
    }, extras)
  }, [
    category,
    currency,
    name,
    offerId,
    position,
    price,
    tracking?.listId,
    tracking?.listName,
    tracking?.lere,
  ])

  const onClick = useCallback(() => {
    fireInteractionEvent({
      ...trackingCtx,
      action: 'Click',
      value: position,
    })
    fireProductClick()
  }, [trackingCtx, position, fireProductClick])

  const onAuthClick = useCallback((e) => {
    e.preventDefault()
    fireInteractionEvent({
      ...trackingCtx,
      action: 'Login Click',
    })
    dispatch(accountAccessShowLogin())
  }, [dispatch, trackingCtx])

  const onClickSecureDeposit = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const showHotelPrice = pricing && !flights?.bundledWithFlightsOnly
  const showBundledHotelPrice = pricing && flights?.bundledWithFlightsOnly

  const distanceText = useMemo(() => {
    if (!distance || !distanceFrom || !distanceUnit) {
      return null
    }
    return getDistanceLabel(distance, distanceFrom, distanceUnit)
  }, [distance, distanceUnit, distanceFrom])

  if (!shouldDisplay(offerType)) {
    return null
  }

  const wall = (
    <TextLink onClick={onAuthClick}>
      <Caption variant="medium" as="span" weight="bold">
        Become a member to see prices
      </Caption>
    </TextLink>
  )

  return (
    <ClickableLink
      to={url}
      onClick={onClick}
      target="_blank"
    >
      <Root className={cn({ 'tile-border': !removeTileBorder })} data-testid={testId} >
        <Image
          id={image.id}
          alt={image.title}
          width={300}
          dpr={2}
          fit="center"
        />
        <Section direction="vertical" fullWidth>
          {distanceText && <OfferLocationText variant="small" colour="neutral-three" lineClamp={1}>{distanceText}</OfferLocationText>}
          <Caption key={location} variant="medium" lineClamp={1}>{location}</Caption>
          <OfferTitleText variant="small" weight="bold" lineClamp={1}>{title}</OfferTitleText>
          <WalledGardentWrapper>
            <WalledContent enforced={walledGarden} wall={wall}>
              {showHotelPrice &&
                <HotelPriceContent
                  duration={duration}
                  saleUnit={saleUnit}
                  offerType={offerType}
                  price={price}
                  value={value}
                  shouldDisplayValue={shouldDisplayValue}
                />
              }
              {showBundledHotelPrice &&
                <BundledHotelPriceContent
                  bundledWithFlightsOnly={flights.bundledWithFlightsOnly}
                  flightPrices={flights.flightPrices}
                  price={price}
                  value={value}
                  duration={duration}
                />
              }
              {depositShouldDisplay && (
                <DepositTagWrapper>
                  <SecureWithDepositTagAndModal
                    depositType={depositType}
                    depositAmount={depositAmount}
                    depositAmountPercentage={depositAmountPercentage}
                    depositThresholds={depositThresholds}
                    offerType={offerType}
                    onClick={onClickSecureDeposit}
                  />
                </DepositTagWrapper>
              )}
            </WalledContent>
          </WalledGardentWrapper>
        </Section>
      </Root>
    </ClickableLink>
  )
}

export default YouMayAlsoLikeTile
